import { useState, useEffect } from "react";
import { listSchemas } from "@/services/schemaBuilderApi";
import { fetchSchemaAssignments } from "@/services/providersApi";
import { useTelemetry } from "@/hooks/useTelemetry";
import {
  trackEvent,
  trackException,
  startTimer as startTelemetryTimer,
} from "@/services/telemetry";
import type { TargetSchema } from "@/types";

let uploadSchemasCache: TargetSchema[] | null = null;
let uploadSchemasInFlight: Promise<TargetSchema[]> | null = null;

/**
 * The legacy unfiltered list is fetched once per browser session and shared
 * across page mounts; newly published schemas need a page reload to appear.
 */
function loadUploadSchemasOnce(): Promise<TargetSchema[]> {
  if (uploadSchemasCache) return Promise.resolve(uploadSchemasCache);
  if (!uploadSchemasInFlight) {
    const stopTimer = startTelemetryTimer();
    uploadSchemasInFlight = listSchemas(true)
      .then((data) => {
        const uploadable = data
          .filter((s) => s.status === "published" || s.status === "preset")
          .map(
            (s): TargetSchema => ({
              id: s.id,
              name: s.name,
              version: "",
              description: s.description,
              status: s.status as "preset" | "published",
              lastUsedAt: s.lastUsedAt,
            })
          );
        uploadSchemasCache = uploadable;
        trackEvent("schemas_loaded", {
          schemaCount: uploadable.length,
          durationMs: stopTimer(),
          providerScoped: false,
        });
        return uploadable;
      })
      .catch((err) => {
        trackException(err, { context: "schema_load" });
        throw err;
      })
      .finally(() => {
        uploadSchemasInFlight = null;
      });
  }
  return uploadSchemasInFlight;
}

/**
 * Load target schemas for the upload page.
 *
 * `providerId` selects which schema list to load:
 *  - a provider id: restricts the list to that provider's active schema
 *    assignments (PSA) via GET /providers/{id}/schema-assignments, so Upload
 *    only offers schemas an admin has explicitly assigned to it.
 *
 *  - null: provider-aware mode with no provider chosen yet — returns an
 *    empty list rather than the unfiltered catalogue.
 *
 *  - omitted (undefined): legacy unfiltered list of all published + preset
 *    schemas via GET /schemas (kept for callers that have not yet adopted
 *    per-provider scoping).
 */
export function useUploadSchemas(providerId?: string | null) {
  const { track, trackError, startTimer } = useTelemetry();
  const [schemas, setSchemas] = useState<TargetSchema[]>([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (providerId === null) {
      setSchemas([]);
      setError("");
      setIsLoadingSchemas(false);
      return;
    }

    const stopTimer = startTimer();
    setIsLoadingSchemas(true);

    const request = providerId
      ? fetchSchemaAssignments(providerId).then((assignments) => {
          const uploadable = assignments.map(
            (a): TargetSchema => ({
              id: a.schema_id,
              name: a.schema_name ?? a.schema_id,
              version: "",
            })
          );
          track("schemas_loaded", {
            schemaCount: uploadable.length,
            durationMs: stopTimer(),
            providerScoped: true,
          });
          return uploadable;
        })
      : loadUploadSchemasOnce();

    request
      .then((uploadable) => {
        setSchemas(uploadable);
        setError(
          uploadable.length === 0
            ? providerId
              ? "This provider has no schemas assigned yet. Contact an admin to assign one."
              : "No target schemas found. The schema table may be empty or misconfigured."
            : ""
        );
      })
      .catch((err) => {
        setError(String(err));
        trackError(err, { context: "schema_load" });
      })
      .finally(() => setIsLoadingSchemas(false));
  }, [providerId, startTimer, track, trackError]);

  return { schemas, isLoadingSchemas, error, setError };
}
